/**
 * Resolving a human-readable string from a chain of candidates, where a blank
 * candidate must NOT win.
 *
 * ── WHY THIS EXISTS, AND WHY `??` IS THE WRONG OPERATOR HERE ────────────────
 *
 * `??` falls through on `null` and `undefined` and on nothing else. `""` is
 * neither, so in
 *
 *     const name = input.name?.trim() ?? fetched.name ?? "Untitled";
 *
 * an empty string WINS and every later candidate — including an expensively
 * fetched one and the constant at the end — is unreachable. The expression
 * reads as a fallback chain and is not one. `tsc` cannot see it: `string`
 * satisfies `string`, so the type is correct at every link and the defect is
 * invisible to typecheck, to lint and to any test that happens to pass a
 * non-blank value.
 *
 * The house rule this encodes: **a label derived from a provider, a database
 * column, an LLM or a request body needs a BLANKNESS test, never a nullish
 * one.** A blank name is present and unusable, and treating it as present is
 * how a fetched fallback becomes dead code.
 *
 * ── WHAT "BLANK" MEANS, AND WHY TRIM ────────────────────────────────────────
 *
 * Blank is `null`, `undefined`, or a string that is empty after `trim()`. The
 * whitespace half is load-bearing: `" "` is what a text input posts when a
 * user taps space and gives up, and a whitespace-only display name masks a
 * perfectly good machine name exactly as `""` does.
 *
 * ── WHAT `trim()` DOES NOT STRIP, AND WHY THAT IS DELIBERATE ────────────────
 *
 * `String.prototype.trim` removes the ECMAScript `WhiteSpace` production plus
 * line terminators. Measured on Node v24.9.0 — do not reason about this from
 * the Unicode category, the two sets do not line up:
 *
 * - **STRIPPED**, and easy to assume otherwise: U+00A0 NBSP, U+1680, U+2003,
 *   U+202F, U+205F, U+3000, and **U+FEFF BOM / ZWNBSP**. A leading BOM is
 *   removed. Do not group the BOM with the zero-width characters below — it
 *   behaves like a space here and they do not.
 * - **NOT STRIPPED**: the zero-width format characters — U+200B ZWSP,
 *   U+200C ZWNJ, U+200D ZWJ, U+2060 WORD JOINER.
 *
 * So a candidate consisting only of, say, one ZWSP is treated as PRESENT and
 * returned. **That is deliberate and must stay.** ZWNJ and ZWJ are meaningful
 * letters, not decoration: Persian and several Indic scripts need ZWNJ for
 * correct orthography, and ZWJ is load-bearing inside emoji sequences. A
 * blankness test that swallowed them would refuse a name a person genuinely
 * typed and silently substitute a fallback — a worse failure than the one this
 * module exists to fix, and an unfixable one from the user's side.
 *
 * ── THIS PRIMITIVE IS STRICTER THAN THE DATABASE, AND THAT IS THE DECISION ──
 *
 * It is NOT in agreement with the storage it feeds, and reading it that way is
 * how the zero-width admission above gets mistaken for the reason. The four
 * `name`-column CHECKs in `20260813010000_name_columns_reject_blank_strings`
 * strip a set of exactly SIX ASCII characters — space, TAB, LF, CR, FF, VT —
 * so every other character in the STRIPPED list above is one this function
 * calls BLANK and those columns happily ACCEPT: NBSP, the BOM, U+1680,
 * U+2000–U+200A, U+2028/9, U+202F, U+205F, U+3000. Measured on node v24.9.0
 * against that six-character set: **19 characters where this primitive is
 * stricter, ZERO in the other direction.**
 *
 * Zero is the number that matters, and it is structural rather than a sample:
 * all six ASCII blanks are members of ECMAScript `WhiteSpace`, so a value this
 * function calls PRESENT can never be one those columns refuse. That is the
 * invariant the migration states in its own §5 — *"there are ZERO values this
 * CHECK refuses that the schema accepts"* — with the database as a FLOOR, never
 * a second validator that can refuse what the API promised to take. Only the
 * LOOSE direction is a defect: it surfaces as an unmapped SQLSTATE 23514 and a
 * 500 on a write the caller was told would be accepted.
 *
 * So the reason not to widen the test is the orthography argument above — ZWNJ
 * and ZWJ are letters people type — and never a storage one. Widening moves
 * further in the safe direction; it just refuses names that are real.
 *
 * ── THE RETURNED VALUE IS TRIMMED. THE FALLBACK IS NOT. ─────────────────────
 *
 * A candidate is trimmed before it is tested and the trimmed form is what is
 * returned, so a caller never has to write `.trim()` twice and can never test
 * one form while storing the other. `firstNonBlankOr`'s `fallback` is returned
 * VERBATIM and is never blank-tested — that is what makes its return type
 * honestly `string` rather than `string | undefined`.
 *
 * So the fallback slot is for a value you are willing to return unexamined:
 * a copy constant (`"Untitled Connection"`), or a `NOT NULL` column that is
 * the terminal by contract (`Collection.name` is `@unique` and non-nullable).
 * A runtime value that might itself be blank belongs in the candidate array
 * instead, where it is tested like every other candidate.
 *
 * ── THE FALLBACK IS EVALUATED EAGERLY. `??` SHORT-CIRCUITS. ────────────────
 *
 * Candidates and the fallback are ordinary arguments, so every one of them is
 * evaluated before the call. `a ?? b()` does not call `b` when `a` is present;
 * `firstNonBlankOr([a], b())` always does. For a pure, cheap fallback — a
 * constant, a string transform of a value already in hand — that costs
 * nothing. For an expensive or side-effecting one, keep the laziness and write
 * `firstNonBlank([a]) ?? expensive()`; the `??` there is operating on
 * `undefined` from this function, which is the one thing `??` is right about.
 *
 * ── PICKING BETWEEN THE TWO ─────────────────────────────────────────────────
 *
 * `firstNonBlank` when "nothing is usable" is a state the caller must render
 * or refuse differently (`undefined`), and `firstNonBlankOr` when a constant
 * closes the chain. Do not spell the second as `firstNonBlank([...]) ?? "X"` —
 * it works, and it re-introduces the operator this module exists to replace at
 * the one position where a reader will copy it.
 *
 * @see present-number.ts for the numeric sibling, where `0` is the legal value
 *      that `||` destroys.
 */

/** A string candidate as it arrives from Prisma, a request body or a provider. */
export type MaybeText = string | null | undefined;

/**
 * True when `value` carries no usable text — `null`, `undefined`, or empty
 * after `trim()`.
 *
 * Use it where the answer is a decision rather than a value: a `filter`, a
 * guard, a refusal. Where the answer is the string itself, use
 * `firstNonBlank`, which tests and trims in one pass.
 */
export function isBlank(value: MaybeText): boolean {
  return value === null || value === undefined || value.trim().length === 0;
}

/**
 * The boundary reader: `value` trimmed when it really is a non-blank string,
 * and `undefined` for everything else — a number, an object, `null`, a blank.
 *
 * This is what you write instead of `as string` when a candidate comes out of
 * a `Record<string, unknown>`: an LLM's JSON, a Prisma `Json` column, a
 * webhook body. `taskMetadata.TaskName as string` compiles, is unchecked, and
 * hands `firstNonBlank` a value that may not be a string at all — and the cast
 * makes the crossing invisible to every source-scanning gate.
 *
 * Deliberately NOT folded into `firstNonBlank`: widening that function to
 * `unknown` would turn a caller's type error into a silent `undefined`. Where
 * the type is already `string | null | undefined`, do not reach for this.
 *
 * ```ts
 * firstNonBlankOr([asNonBlankText(metadata.TaskName)], "AI Task");
 * ```
 */
export function asNonBlankText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The first candidate that carries usable text, trimmed, or `undefined` when
 * none does.
 *
 * ```ts
 * // "" no longer beats the fetched name
 * firstNonBlank([input.name, fetched.connectionName]);
 * ```
 *
 * @param candidates in preference order, best first.
 */
export function firstNonBlank(candidates: readonly MaybeText[]): string | undefined {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const trimmed = candidate.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return undefined;
}

/**
 * `firstNonBlank`, closed by a constant, so the result is always a `string`.
 *
 * ```ts
 * firstNonBlankOr([input.name, fetched.connectionName], "Untitled Connection");
 * ```
 *
 * @param candidates in preference order, best first.
 * @param fallback returned verbatim when no candidate is usable. It is NOT
 *   blank-tested and NOT trimmed; pass a constant.
 */
export function firstNonBlankOr(candidates: readonly MaybeText[], fallback: string): string {
  const resolved = firstNonBlank(candidates);
  return resolved === undefined ? fallback : resolved;
}
